import React, { useReducer } from 'react';

import { css } from '@emotion/css';
import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles.ts';
import { HandledErrorPage } from '@/pages/HandledErrorPage.tsx';
import { QueryAwait } from '@/queries/QueryAwait.tsx';
import { SideSheet } from '@/uiKit/atoms/SideSheet/SideSheet.tsx';
import { CardHeader } from '@/uiKit/atoms/card/CardHeader.tsx';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { CarePlanCreationForm } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreationForm.tsx';
import { CarePlanCreationFormActions } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreationFormActions.tsx';
import { CarePlanCreationFormContent } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreationFormContent.tsx';
import {
  useCreateNoInsulinPatient,
  useCreatePatientAndCarePlan,
  useGeneratePrescription,
  useSignPrescriptionAndCreatePatient,
} from '@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationAndRenewalActions.ts';
import { patientCreationWizard } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationWizard.ts';
import { PatientInfoForm } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfoForm.tsx';
import { PatientInfoFormActions } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfoFormActions.tsx';
import { PatientInfoFormContent } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfoFormContent.tsx';
import { PrescriptionInfoForm } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoForm.tsx';
import { PrescriptionInfoFormActions } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormActions.tsx';
import { PrescriptionInfoFormContent } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormContent.tsx';
import { PrescriptionInfoFormTitle } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormTitle.tsx';
import { PrescriptionSignActions } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignActions.tsx';
import { PrescriptionSignContent } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignContent.tsx';

export type PatientCreationProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (patientId: string) => void;
};

export const PatientCreation: React.FC<PatientCreationProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(
    patientCreationWizard.reducer,
    patientCreationWizard.initialState,
  );
  const generatePrescription = useGeneratePrescription();
  const createNoInsulinPatient = useCreateNoInsulinPatient(createdPatientId =>
    onSubmit(createdPatientId.id),
  );
  const createPatientAndCarePlan = useCreatePatientAndCarePlan(
    createdPatientId => onSubmit(createdPatientId.id),
  );
  const signPrescriptionAndCreatePatient = useSignPrescriptionAndCreatePatient(
    createdPatientId => onSubmit(createdPatientId.id),
  );

  return (
    <SideSheet open={open} onClose={onClose}>
      <CardHeader title={t('pages.patients.addPatient')} variant="contained" />
      <QueryAwait
        errorFallback={HandledErrorPage}
        pendingFallback={CenteredLoader}
      >
        {state.step === 'PatientInfo' && (
          <PatientInfoForm
            patientInfo={state.PatientInfo ?? {}}
            onSubmit={data =>
              dispatch({
                action: 'TO_CarePlan',
                data,
              })
            }
            className={styles.form}
          >
            <PatientInfoFormContent />
            <PatientInfoFormActions
              onBack={data => {
                dispatch({ action: 'SET_DATA_PatientInfo', data });
                onClose();
              }}
            />
          </PatientInfoForm>
        )}
        {state.step !== 'PatientInfo' && (
          <CarePlanCreationForm
            carePlanInfo={state.CarePlan}
            onPrescription={data =>
              dispatch({ action: 'TO_PrescriptionInfo', data })
            }
            onCreateNoInsulin={data =>
              createNoInsulinPatient(state.PatientInfo, data)
            }
            className={styles.form}
          >
            <CarePlanCreationFormContent patientInfo={state.PatientInfo} />
            <CarePlanCreationFormActions
              onBack={data => dispatch({ action: 'BACK_TO_PatientInfo', data })}
            />
          </CarePlanCreationForm>
        )}
        <Dialog
          open={
            state.step === 'PrescriptionInfo' ||
            state.step === 'PrescriptionSign'
          }
          onClose={() => {
            if (state.step === 'PrescriptionSign') {
              dispatch({ action: 'BACK_TO_PrescriptionInfo' });
            }
            dispatch({ action: 'BACK_TO_CarePlan' });
          }}
          maxWidth={state.step === 'PrescriptionInfo' ? 'xs' : 'md'}
          fullWidth
          classes={{ paper: styles.dialog }}
        >
          {state.step === 'PrescriptionInfo' && (
            <PrescriptionInfoForm
              prescriptionInfo={state.PrescriptionInfo ?? {}}
              onGeneratePrescription={data => {
                generatePrescription(
                  {
                    patientInfo: state.PatientInfo,
                    carePlan: state.CarePlan,
                    prescriptionInfo: data,
                  },
                  ({ prescription }) =>
                    dispatch({
                      action: 'TO_PrescriptionSign',
                      data: { ...data, prescription },
                    }),
                );
              }}
              onUploadPrescription={file =>
                createPatientAndCarePlan(
                  state.PatientInfo,
                  state.CarePlan,
                  state.PrescriptionInfo ?? {},
                  file,
                )
              }
            >
              <PrescriptionInfoFormTitle
                onExit={data => dispatch({ action: 'BACK_TO_CarePlan', data })}
              />
              {state.step === 'PrescriptionInfo' && (
                <PrescriptionInfoFormContent patientInfo={state.PatientInfo} />
              )}
              <PrescriptionInfoFormActions
                onBack={data => dispatch({ action: 'BACK_TO_CarePlan', data })}
                submitTitle={t('diabetesForms.addPatient')}
              />
            </PrescriptionInfoForm>
          )}
          {state.step === 'PrescriptionSign' && (
            <>
              <CardHeader
                title={t('diabetesForms.prescriptionSignature')}
                variant="contained"
                onExit={() => {
                  dispatch({
                    action: 'BACK_TO_PrescriptionInfo',
                  });
                  dispatch({ action: 'BACK_TO_CarePlan' });
                }}
              />
              <PrescriptionSignContent
                prescription={state.PrescriptionInfo.prescription}
              />
              <PrescriptionSignActions
                submitTitle={t('diabetesForms.signAndAddPatient')}
                onBack={() => {
                  dispatch({
                    action: 'BACK_TO_PrescriptionInfo',
                  });
                }}
                onSubmit={() =>
                  signPrescriptionAndCreatePatient(
                    state.PatientInfo,
                    state.CarePlan,
                    state.PrescriptionInfo,
                  )
                }
              />
            </>
          )}
        </Dialog>
      </QueryAwait>
    </SideSheet>
  );
};

const makeStyles = () => ({
  form: css({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
  }),
  dialog: css({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
  }),
});
